import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Card, Icon, Link, Text } from '@factorialco/deprecated-design-system'
import { Moment } from 'moment'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { SegmentedControl } from 'modules/project_management/components/SegmentedControl'
import { CostDistributionChart } from 'modules/project_management/components/charts/DistributionChart/CostDistributionChart'
import TimeDistributionChart from 'modules/project_management/components/charts/DistributionChart/TimeDistributionChart'
import {
  type graphViewsDetailViewOptionEnum,
  overviewFrequencyOptionsEnum,
} from 'modules/project_management/lib/constants'
import { OverviewFilterOption } from 'modules/project_management/lib/overviewOptions'

interface DistributionChartProps {
  projectId: string
  startDate?: Moment
  endDate?: Moment
  selectedFrequency: overviewFrequencyOptionsEnum
  selectedFilter: OverviewFilterOption
  canSeeLaborCosts: boolean
  currency: string
}

export const DistributionChart: React.FC<DistributionChartProps> = ({
  projectId,
  startDate,
  endDate,
  selectedFilter,
  selectedFrequency,
  canSeeLaborCosts,
  currency,
}) => {
  const [currentChart, setCurrentChart] = useState<number>(0)
  const [selectedOption, setSelectedOption] = useState<graphViewsDetailViewOptionEnum>('employees')

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const segments = [
    { label: i18n.t('project_management.graphs.segments.hours.title') },
    { label: i18n.t('project_management.graphs.segments.cost.title') },
  ]

  const changeChart = (index: number) => {
    setCurrentChart(index)
  }

  const handleOnClick = () => {
    switch (selectedOption) {
      case 'employees':
        return navigate(
          navigation.projects.id.details.employees.fullPath({ id: String(projectId) })
        )
      case 'subprojects':
        return navigate(
          navigation.projects.id.details.subprojects.fullPath({ id: String(projectId) })
        )
    }
  }

  useEffect(() => {
    track(trackingEvents.projectManagement.overview.seeDistributionChart, {
      data_information: currentChart === 0 ? 'hours' : 'cost',
      distribution_by: selectedOption,
    })
  }, [currentChart, selectedOption])

  return (
    <Card>
      <Card.Content>
        <>
          <Box flexDirection="row" justifyContent="spaceBetween" width="full">
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Box maxWidth="s600" flexDirection="row" alignItems="center" gap="s6">
                  <Text.Header2 weight="normal">
                    {i18n.t('project_management.graphs.details.title')}
                  </Text.Header2>
                  <Box flexDirection="row" alignItems="center" gap="s6">
                    <Text.Header2>
                      {selectedOption === 'employees'
                        ? i18n.t('project_management.graphs.details.employee')
                        : i18n.t('project_management.graphs.details.subproject')}
                    </Text.Header2>
                    <Icon.Small icon="chevron-down" color="grey1000" />
                  </Box>
                </Box>
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="start">
                <PopoverMenu.TouchableOption
                  key="employees"
                  title={i18n.t('project_management.graphs.details.employee')}
                  onClick={() => setSelectedOption('employees')}
                />
                <PopoverMenu.TouchableOption
                  key="subprojects"
                  title={i18n.t('project_management.graphs.details.subproject')}
                  onClick={() => setSelectedOption('subprojects')}
                />
              </PopoverMenu.Content>
            </PopoverMenu>
            {canSeeLaborCosts && (
              <SegmentedControl
                segments={segments}
                onChangeSelected={changeChart}
                layoutId="distributionChart"
              />
            )}
          </Box>
          <Box gap="s12" width="full" paddingTop="s12">
            <Text color={'grey800'} weight="medium" size="300">
              {selectedFilter.subtitle}
            </Text>
            {currentChart === 0 ? (
              <TimeDistributionChart
                projectId={projectId}
                startDate={startDate}
                endDate={endDate}
                frequencySelected={selectedFrequency}
                selectedOption={selectedOption}
              />
            ) : (
              <CostDistributionChart
                projectId={projectId}
                startDate={startDate}
                endDate={endDate}
                selectedFrequency={selectedFrequency}
                selectedOption={selectedOption}
                currency={currency}
              />
            )}
            <Box paddingTop="s24">
              <Link.Touchable onClick={handleOnClick}>
                {i18n.t('project_management.graphs.details.view_detail')}
              </Link.Touchable>
            </Box>
          </Box>
        </>
      </Card.Content>
    </Card>
  )
}
